import { baseApi } from "@/shared/api";
import type {
  EventParticipant,
  EventParticipantInput,
  ParticipantCandidate,
  ProjectEvent,
  ProjectEventPayload,
  ProjectTaskOption,
} from "../types";

interface ProjectScopedArg {
  projectId: number;
}

interface EventArg extends ProjectScopedArg {
  eventId: number;
}

interface CreateEventArg extends ProjectScopedArg {
  body: ProjectEventPayload;
}

interface UpdateEventArg extends EventArg {
  body: Partial<ProjectEventPayload>;
}

interface ReplaceParticipantsArg extends EventArg {
  participants: EventParticipantInput[];
}

interface UploadPhotosArg extends EventArg {
  files: File[];
}

interface DeletePhotoArg extends EventArg {
  key: string;
}

export const eventsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjectEvents: builder.query<ProjectEvent[], ProjectScopedArg>({
      query: ({ projectId }) => ({
        url: `/api/projects/${projectId}/events/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: "ProjectEvents", id: projectId },
      ],
    }),
    getProjectEvent: builder.query<ProjectEvent, EventArg>({
      query: ({ projectId, eventId }) => ({
        url: `/api/projects/${projectId}/events/${eventId}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, { eventId }) => [
        { type: "ProjectEvents", id: `EVENT-${eventId}` },
      ],
    }),
    getParticipantCandidates: builder.query<ParticipantCandidate[], ProjectScopedArg>({
      query: ({ projectId }) => ({
        url: `/api/projects/${projectId}/participant-candidates/`,
        method: "GET",
        credentials: "include" as const,
      }),
    }),
    getProjectTaskOptions: builder.query<ProjectTaskOption[], ProjectScopedArg>({
      query: ({ projectId }) => ({
        url: `/api/projects/${projectId}/tasks/`,
        method: "GET",
        credentials: "include" as const,
      }),
      transformResponse(rows: ProjectTaskOption[]) {
        return rows.map((row) => ({ id: row.id, title: row.title, task_type: row.task_type }));
      },
    }),
    createProjectEvent: builder.mutation<ProjectEvent, CreateEventArg>({
      query: ({ projectId, body }) => ({
        url: `/api/projects/${projectId}/events/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "ProjectEvents", id: projectId },
      ],
    }),
    updateProjectEvent: builder.mutation<ProjectEvent, UpdateEventArg>({
      query: ({ projectId, eventId, body }) => ({
        url: `/api/projects/${projectId}/events/${eventId}/`,
        method: "PATCH",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId, eventId }) => [
        { type: "ProjectEvents", id: projectId },
        { type: "ProjectEvents", id: `EVENT-${eventId}` },
      ],
    }),
    deleteProjectEvent: builder.mutation<void, EventArg>({
      query: ({ projectId, eventId }) => ({
        url: `/api/projects/${projectId}/events/${eventId}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "ProjectEvents", id: projectId },
      ],
    }),
    replaceEventParticipants: builder.mutation<EventParticipant[], ReplaceParticipantsArg>({
      query: ({ projectId, eventId, participants }) => ({
        url: `/api/projects/${projectId}/events/${eventId}/participants/`,
        method: "POST",
        body: participants,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { projectId, eventId }) => [
        { type: "ProjectEvents", id: projectId },
        { type: "ProjectEvents", id: `EVENT-${eventId}` },
      ],
    }),
    uploadEventPhotos: builder.mutation<ProjectEvent, UploadPhotosArg>({
      query: ({ projectId, eventId, files }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        return {
          url: `/api/projects/${projectId}/events/${eventId}/photos/`,
          method: "POST",
          body: formData,
          credentials: "include" as const,
        };
      },
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: "ProjectEvents", id: `EVENT-${eventId}` },
      ],
    }),
    deleteEventPhoto: builder.mutation<ProjectEvent, DeletePhotoArg>({
      query: ({ projectId, eventId, key }) => ({
        url: `/api/projects/${projectId}/events/${eventId}/photos/delete/?key=${encodeURIComponent(key)}`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: "ProjectEvents", id: `EVENT-${eventId}` },
      ],
    }),
  }),
});

export const {
  useGetProjectEventsQuery,
  useGetProjectEventQuery,
  useGetParticipantCandidatesQuery,
  useGetProjectTaskOptionsQuery,
  useCreateProjectEventMutation,
  useUpdateProjectEventMutation,
  useDeleteProjectEventMutation,
  useReplaceEventParticipantsMutation,
  useUploadEventPhotosMutation,
  useDeleteEventPhotoMutation,
} = eventsApi;
